// api/common/MessagesHub.ts
import * as signalR from '@microsoft/signalr';
import { API_BASE_URL } from '../config';

/**
 * Minimal, direct SignalR client for `CrmChatHub` (/hubs/chat)'s `OnMessageReceived` event —
 * deliberately NOT built on `SignalRServiceBase`/`ChatHubService`. That base class is untested
 * boilerplate (its `heartbeat()` invokes a `Heartbeat` hub method that doesn't exist on any
 * real ServiceVault hub — they expose `Ping()` — and its transport-fallback headers carry a
 * hardcoded `AgroRange-ReactNative` user agent from an unrelated project), and nothing in this
 * codebase has ever actually instantiated `ChatHubService` (only its payload *type* is
 * imported, in admin-dashboard, by a hook — `useContactNotifications` — that doesn't exist in
 * the repo). Module 14 needed something proven, not another layer on unverified plumbing.
 */
export interface MessageReceivedPayload {
  conversationId: string;
  id: string;
  senderUserId: string;
  isFromStaff: boolean;
  body: string;
  sentAt: string;
}

/** Connects and starts listening. Caller owns the connection — call `.stop()` on cleanup. */
export function connectMessagesHub(
  accessToken: string,
  onMessage: (payload: MessageReceivedPayload) => void
): signalR.HubConnection {
  const connection = new signalR.HubConnectionBuilder()
    .withUrl(`${API_BASE_URL}/hubs/chat`, { accessTokenFactory: () => accessToken })
    .withAutomaticReconnect()
    .configureLogging(signalR.LogLevel.Warning)
    .build();

  connection.on('OnMessageReceived', onMessage);

  connection.start().catch((err) => {
    console.error('[MessagesHub] connection failed:', err);
  });

  return connection;
}
